import React, { useState, useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/shared/card';
import { Button } from '../../../components/shared/button';

export interface PodcastHistoryItem {
  id: number;
  title: string;
  date: string;
  audioUrl: string;
}

interface PodcastHistoryProps {
  history: PodcastHistoryItem[];
}

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const PodcastHistory: React.FC<PodcastHistoryProps> = ({ history }) => {
  const [visibleCount, setVisibleCount] = useState(10);
  const [loading, setLoading] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!listRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = listRef.current;
      if (scrollHeight - scrollTop - clientHeight < 100 && visibleCount < history.length && !loading) {
        setLoading(true);
        setTimeout(() => {
          setVisibleCount((prev) => Math.min(prev + 10, history.length));
          setLoading(false);
        }, 500);
      }
    };
    const ref = listRef.current;
    if (ref) {
      ref.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (ref) {
        ref.removeEventListener('scroll', handleScroll);
      }
    };
  }, [visibleCount, history.length, loading]);

  if (!history.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-500">
        <span className="text-6xl mb-4">🎧</span>
        <div className="text-lg font-semibold mb-2">No podcasts found</div>
        <div className="text-sm text-gray-400">Your generated podcasts will appear here.</div>
      </div>
    );
  }

  return (
    <div ref={listRef} className="max-w-2xl mx-auto p-4 overflow-y-auto" style={{ maxHeight: '80vh' }}>
      <h2 className="text-3xl font-extrabold mb-8 text-center tracking-tight">Podcast History</h2>
      <ul className="space-y-8">
        {history.slice(0, visibleCount).map((podcast, idx) => (
          <React.Fragment key={podcast.id}>
            <Card className="bg-gray-50 transition-all duration-200 hover:shadow-2xl hover:-translate-y-1 border border-gray-200 p-6 rounded-2xl shadow-md">
              <CardHeader className="pb-2 flex flex-row items-center gap-2">
                <span className="text-2xl">🎙️</span>
                <CardTitle className="text-xl font-bold mb-1 tracking-tight">{podcast.title}</CardTitle>
              </CardHeader>
              <div className="px-6 text-gray-400 text-xs font-medium mb-2">{formatDate(podcast.date)}</div>
              <hr className="mx-6 my-0 border-gray-100" />
              <CardContent className="pt-6 pb-2 px-0">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-gray-100 rounded-lg p-3 transition-shadow hover:shadow-md">
                  <div className="flex-1 flex items-center">
                    <audio controls src={podcast.audioUrl} className="w-full md:w-56" />
                  </div>
                  <Button asChild size="sm" className="w-full md:w-auto mt-2 md:mt-0 flex items-center gap-2 bg-gradient-to-r from-blue-700 to-blue-500 hover:from-blue-800 hover:to-blue-600 transition-transform duration-150 hover:scale-110 hover:shadow-lg focus:scale-110" title="Download this podcast">
                    <a href={podcast.audioUrl} download={podcast.title + '.mp3'}>
                      <span role="img" aria-label="download">⬇️</span> Download
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
            {idx < Math.min(visibleCount, history.length) - 1 && (
              <div className="w-full flex justify-center">
                <div className="h-4 border-l-2 border-dotted border-gray-200 my-2" style={{ height: '2rem' }} />
              </div>
            )}
          </React.Fragment>
        ))}
      </ul>
      {loading && (
        <div className="flex justify-center py-4 text-gray-400">Loading more...</div>
      )}
    </div>
  );
};

export default PodcastHistory;